import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";
import chat from "../../api/chat";
import { Case } from "../../interfaces/api/chat";
import Button from "../button";
import { ButtonVariant } from "../../shared/utils/enums";

interface CasesModalProps {
  onSelectCase: (caseValue: string) => void;
  onClose: () => void;
}

// Skeleton loader component for cases
const CaseSkeleton = () => (
  <div className="w-full p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 animate-pulse">
    <div className="flex items-center justify-between">
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
      <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
    </div>
  </div>
);

const CasesModal: React.FC<CasesModalProps> = ({ onSelectCase, onClose }) => {
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);

  useEffect(() => {
    const fetchCases = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await chat.getCases();
        if (response.result === "success") {
          setCases(response.content);
        } else {
          setError(response.error_description || "Failed to load cases");
        }
      } catch (err) {
        setError("Failed to load cases. Please try again.");
        console.error("Error fetching cases:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCases();
  }, []);

  const handleCaseSelect = (selectedCase: Case) => {
    setSelectedCase(selectedCase);
  };

  const handleSave = () => {
    if (selectedCase) {
      onSelectCase(selectedCase.value);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-6 p-4">
        <div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {/* Show 6 skeleton loaders while loading */}
            {Array.from({ length: 2 }).map((_, index) => (
              <CaseSkeleton key={index} />
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
          <Button
            variant={ButtonVariant.SECONDARY}
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:focus:ring-gray-400 transition-colors duration-200"
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            disabled
            className="px-4 py-2 text-sm font-medium text-gray-400 bg-gray-300 dark:bg-gray-700 rounded-md cursor-not-allowed"
          >
            Select Case
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-6 p-4">
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={onClose}
            className="mt-4 px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-4">
      <div>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {cases.map((caseItem) => (
            <button
              key={caseItem.id}
              onClick={() => handleCaseSelect(caseItem)}
              className={`w-full text-left p-3 rounded-lg border transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-800 ${
                selectedCase?.id === caseItem.id
                  ? "border-primary bg-primary/5 dark:bg-primary/10"
                  : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {caseItem.label}
                </span>
                {selectedCase?.id === caseItem.id && (
                  <Check className="w-4 h-4 text-primary" />
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
        <Button
          variant={ButtonVariant.SECONDARY}
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:focus:ring-gray-400 transition-colors duration-200"
        >
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          onClick={handleSave}
          disabled={!selectedCase}
          className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
        >
          Select Case
        </Button>
      </div>
    </div>
  );
};

export default CasesModal;
